import type { FeedbackSubmissionInput, PublicProjectConfig } from '@feedback/shared';
import { readBootConfig, type BootConfig } from './boot-config.js';
import { createShell, type Shell } from './shell.js';
import { createPanel, type Panel } from './ui/panel.js';
import { el, icons, svg } from './ui/dom.js';
import { captureScreenshot } from './capture.js';
import { fetchProjectConfig, submitFeedback } from './api.js';
import { installConsoleCollector, getConsoleEntries } from './collectors/console.js';
import { installErrorCollector, getJsErrors } from './collectors/errors.js';
import { installNetworkCollector, getFailedRequests } from './collectors/network.js';
import { collectEnvironment } from './collectors/environment.js';

/** Public surface exposed on `window.feedbackWidget`. */
interface WidgetApi {
  open(): void;
  close(): void;
  identify(traits: Record<string, string>): void;
  version: string;
}

declare global {
  interface Window {
    feedbackWidget?: WidgetApi;
    __feedbackWidgetLoaded?: boolean;
  }
}

const VERSION = '0.1.0';

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not decode the captured screenshot'));
    image.src = dataUrl;
  });
}

async function start(boot: BootConfig): Promise<void> {
  /*
   * Collectors are installed before anything else so they observe the page for
   * as long as possible. A report filed at 11:00 about an error thrown at 09:00
   * is only useful if we were listening at 09:00.
   */
  installConsoleCollector();
  installErrorCollector();
  installNetworkCollector(boot.apiBase);

  let projectConfig: PublicProjectConfig | null = null;
  try {
    projectConfig = await fetchProjectConfig(boot.apiBase, boot.projectId);
  } catch {
    // A config fetch failure must not remove the ability to report a bug — that
    // is exactly when someone most wants to tell us something is broken.
  }

  const theme = projectConfig?.theme;
  const accent = theme?.accent ?? '#5b5bd6';
  const position = boot.position ?? theme?.position ?? 'bottom-right';
  const launcherLabel = theme?.launcherLabel ?? 'Feedback';

  const shell: Shell = createShell();
  shell.setAccent(accent);

  let identity: Record<string, string> = {};
  let panel: Panel | null = null;

  const launcher = el('button', {
    class: `launcher launcher--${position}`,
    type: 'button',
    'aria-label': `${launcherLabel} — report a problem with this page`,
  }, svg(icons.bug, 15), launcherLabel);

  if (!boot.showLauncher) launcher.hidden = true;
  launcher.addEventListener('click', () => void openPanel());
  shell.root.append(launcher);

  async function runCapture(): Promise<void> {
    if (!panel) return;
    panel.showCapturing();

    // Let the browser paint the dialog before the capture blocks the main thread,
    // otherwise the reporter sees a frozen page with no explanation.
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));

    try {
      const result = await captureScreenshot({ apiBase: boot.apiBase });
      const image = await loadImage(result.dataUrl);
      panel.showScreenshot(image, result.warnings);
    } catch (error) {
      panel.showCaptureFailed(
        error instanceof Error ? error.message : 'The page could not be captured.',
      );
    }
  }

  async function openPanel(): Promise<void> {
    if (!panel) {
      panel = createPanel(
        {
          onSubmit: async (input) => {
            const metadata = await collectEnvironment();
            const payload: FeedbackSubmissionInput = {
              projectId: boot.projectId,
              title: input.title,
              description: input.description,
              type: input.type,
              severity: input.severity,
              ...(Object.keys(input.fieldValues).length > 0
                ? { fieldValues: input.fieldValues }
                : {}),
              reporter: {
                ...(input.reporterName ? { name: input.reporterName } : {}),
                ...(input.reporterEmail ? { email: input.reporterEmail } : {}),
                ...(Object.keys(identity).length > 0 ? { identity } : {}),
              },
              ...(input.screenshot ? { screenshot: input.screenshot } : {}),
              metadata,
              diagnostics: {
                consoleEntries: getConsoleEntries(),
                jsErrors: getJsErrors(),
                failedRequests: getFailedRequests(),
              },
            };
            return submitFeedback(boot.apiBase, payload);
          },
          onRetake: runCapture,
          onClose: () => {
            panel?.close();
            launcher.hidden = !boot.showLauncher;
          },
        },
        accent,
        {
          // Seed the reporter fields from identify() traits (e.g. a signed-in
          // user's name/email), so the host app can prefill the form. All traits
          // still travel on the report as reporter.identity.
          reporterDefaults: { name: identity.name, email: identity.email },
          fields: projectConfig?.fields ?? [],
          pathHints: projectConfig?.pathHints ?? [],
        },
      );
      shell.root.append(panel.element);
    }

    // Hide the launcher during capture as a second line of defence; the capture
    // pipeline also excludes the whole widget by selector.
    launcher.hidden = true;
    panel.open();
    panel.setMetadata(await collectEnvironment());
    await runCapture();
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && panel?.isOpen()) {
      panel.close();
      launcher.hidden = !boot.showLauncher;
    }
  });

  window.feedbackWidget = {
    open: () => void openPanel(),
    close: () => {
      panel?.close();
      launcher.hidden = !boot.showLauncher;
    },
    identify: (traits) => {
      identity = { ...identity, ...traits };
    },
    version: VERSION,
  };
}

/* ------------------------------------------------------------------ boot */

function boot(): void {
  // Two copies of the widget on one page would double-patch fetch and stack two
  // launchers; the first one loaded wins.
  if (window.__feedbackWidgetLoaded) return;

  const config = readBootConfig();
  if (!config) {
    console.warn('[feedback-widget] missing data-project on the script tag; not starting.');
    return;
  }

  window.__feedbackWidgetLoaded = true;

  const run = (): void => {
    void start(config).catch((error: unknown) => {
      console.warn('[feedback-widget] failed to start:', error);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
}

boot();
